'use client';

import { useCallback } from 'react';
import { blackTransparentLoaderModalAtom, centerToastModalAtom, confirmModalAtom } from '@/atom/modal';
import { TravelReviewCommentCGType } from '@/types/graphql/root';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { createTravelReviewComment, deleteTravelReviewComment, updateTravelReviewComment } from '@/utils/actions';
import { getDisplayCreatedAt } from '@/utils/date-calc';
import { filterNullUndefined } from '@/utils';
import ReplyComment1010 from '@/svg/sharing-trips/reply_comment_10_10.svg';
import CommentMoreDropdownButton from './comment-more-dropdown-button';

export default function TripPostComment({
  postId,
  comment,
  onClickReportComment,
  isParent,
  isLoggedin,
}: {
  postId: number;
  comment: TravelReviewCommentCGType;
  onClickReportComment: (commentId: number) => void;
  isParent?: boolean;
  isLoggedin: boolean;
}) {
  const router = useRouter();

  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editCommentValue, setEditCommentValue] = useState('');

  const [isReplyMode, setIsReplyMode] = useState(false);
  const [replyCommentValue, setReplyCommentValue] = useState('');

  const onClickUpdateComment = useCallback(() => {
    const updateComment = async () => {
      try {
        setBlackTransparentLoaderModal(true);
        await updateTravelReviewComment(comment.id, {
          content: editCommentValue,
          travelReviewId: postId,
        });
        setIsEditMode(false);
        router.refresh();
      } catch (error) {
        setCenterToastModal({
          text: '댓글 수정에 실패했습니다.',
          renderingTime: 1000,
        });
      } finally {
        setBlackTransparentLoaderModal(false);
      }
    };

    if (editCommentValue === '') {
      setCenterToastModal({
        text: '댓글을 입력해주세요.',
        renderingTime: 1000,
      });
      return;
    }

    setConfirmModal({
      visible: true,
      title: '댓글 수정',
      message: '부적절한 댓글은 삭제될 수 있습니다.',
      onConfirm: () => {
        setConfirmModal({ visible: false });
        updateComment();
      },
      onClose: () => {
        setConfirmModal({ visible: false });
      },
      onOuterClick: () => {
        setConfirmModal({ visible: false });
      },
    });
  }, [editCommentValue]);

  const onClickDeleteComment = useCallback(() => {
    const deleteComment = async () => {
      try {
        setBlackTransparentLoaderModal(true);
        await deleteTravelReviewComment(comment.id);
        router.refresh();
      } catch (error) {
        setCenterToastModal({
          text: '댓글 삭제에 실패했습니다.',
          renderingTime: 1000,
        });
      } finally {
        setBlackTransparentLoaderModal(false);
      }
    };

    setConfirmModal({
      visible: true,
      title: '댓글 삭제',
      message: '댓글을 삭제하시겠습니까?',
      onConfirm: () => {
        setConfirmModal({ visible: false });
        deleteComment();
      },
      onClose: () => {
        setConfirmModal({ visible: false });
      },
      onOuterClick: () => {
        setConfirmModal({ visible: false });
      },
    });
  }, [comment.id]);

  const onClickReplyComment = useCallback(() => {
    if (!isLoggedin) {
      setConfirmModal({
        visible: true,
        title: '로그인 필요',
        message: '로그인으로 이동합니다.',
        onClose: () => {
          setConfirmModal({ visible: false });
        },
        onConfirm: () => {
          setConfirmModal({ visible: false });
          window.location.href =
            process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'user/signin?redirectUrl=' + window.location.href;
        },
      });
      return;
    }
    setIsReplyMode((prev) => !prev);
  }, [isLoggedin]);

  const onClickSubmitReplyComment = useCallback(() => {
    const createReplyComment = async () => {
      try {
        setBlackTransparentLoaderModal(true);
        await createTravelReviewComment({
          travelReviewId: postId,
          content: replyCommentValue,
          parentId: comment.id,
        });
        setReplyCommentValue('');
        setIsReplyMode(false);
        router.refresh();
      } catch (error) {
        setCenterToastModal({
          text: '댓글 등록에 실패했습니다.',
          renderingTime: 1000,
        });
      } finally {
        setBlackTransparentLoaderModal(false);
      }
    };

    if (replyCommentValue === '') {
      setCenterToastModal({
        text: '답글을 입력해주세요.',
        renderingTime: 1000,
      });
      return;
    }

    createReplyComment();
  }, [replyCommentValue]);

  if (isEditMode) {
    return (
      <div className={`w-full`}>
        <div className='w-full px-20pxr flex items-center gap-8pxr border-b border-new-gray-200'>
          <div className='shrink-0 flex items-center justify-center w-20pxr h-20pxr'>
            <ReplyComment1010 />
          </div>
          <div className='flex-1 h-50pxr flex justify-between items-center gap-8pxr'>
            <input
              type='text'
              placeholder={`${isLoggedin ? '불쾌감을 주는 욕설과 악플은 삭제될 수 있습니다.' : '로그인 후 댓글을 작성할 수 있습니다.'}`}
              disabled={!isLoggedin}
              value={editCommentValue}
              onChange={(e) => {
                if (e.target.value.length > 1000) return;
                setEditCommentValue(e.target.value);
              }}
              className={`flex-1 outline-none body-14-regular text-new-gray-900 bg-white placeholder:text-new-gray-500`}
            />

            <div className='flex items-center gap-8pxr'>
              <button
                className={`shrink-0 w-50pxr h-32pxr flex items-center justify-center rounded-6pxr text-white body-14-md bg-new-gray-300`}
                onClick={() => {
                  setIsEditMode(false);
                }}
              >
                취소
              </button>
              <button
                className={`shrink-0 w-50pxr h-32pxr flex items-center justify-center rounded-6pxr text-white body-14-md bg-new-point-500`}
                onClick={() => onClickUpdateComment()}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div key={`post_${postId}_comment_${comment?.id}`} className={`w-full`}>
        <div className='px-20pxr py-16pxr flex items-start gap-8pxr border-b border-new-gray-200'>
          {!isParent && (
            <div className='shrink-0 flex items-center justify-center w-20pxr h-20pxr'>
              <ReplyComment1010 />
            </div>
          )}
          <div className='flex-1 flex flex-col gap-8pxr'>
            <div className='w-full flex items-center justify-between'>
              <div className='flex items-center gap-8pxr'>
                <div className='body-14-md text-new-gray-900'>{comment?.userName}</div>
                <div className='body-12-regular text-new-gray-700'>{getDisplayCreatedAt(comment?.createdAt)}</div>
              </div>

              {comment?.isOwner && (
                <CommentMoreDropdownButton>
                  <div
                    className='absolute z-[2] right-0 top-30pxr border border-new-gray-300 rounded-8pxr flex flex-col bg-white overflow-hidden'
                    style={{
                      boxShadow: '0px 4px 8px 0px rgba(14, 31, 53, 0.12)',
                    }}
                  >
                    {isParent && (
                      <div
                        className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                        onClick={() => {
                          onClickReplyComment();
                        }}
                      >
                        답글달기
                      </div>
                    )}
                    {!comment.isDeleted && (
                      <div
                        className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                        onClick={() => {
                          setEditCommentValue(comment?.content ?? '');
                          setIsEditMode(true);
                        }}
                      >
                        수정하기
                      </div>
                    )}
                    {!comment.isDeleted && (
                      <div
                        className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                        onClick={() => {
                          onClickDeleteComment();
                        }}
                      >
                        삭제하기
                      </div>
                    )}
                  </div>
                </CommentMoreDropdownButton>
              )}
              {!comment?.isOwner && (
                <CommentMoreDropdownButton>
                  <div
                    className='absolute z-[2] right-0 top-30pxr border border-new-gray-300 rounded-8pxr flex flex-col bg-white overflow-hidden'
                    style={{
                      boxShadow: '0px 4px 8px 0px rgba(14, 31, 53, 0.12)',
                    }}
                  >
                    {isParent && (
                      <div
                        className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                        onClick={() => {
                          onClickReplyComment();
                        }}
                      >
                        답글달기
                      </div>
                    )}
                    {!comment.isDeleted && (
                      <div
                        className='py-8pxr px-16pxr body-14-regular text-new-gray-900 whitespace-nowrap cursor-pointer hover:bg-new-point-50'
                        onClick={() => {
                          onClickReportComment(comment.id);
                        }}
                      >
                        신고하기
                      </div>
                    )}
                  </div>
                </CommentMoreDropdownButton>
              )}
            </div>

            {comment.isDeleted ? (
              <div className='w-full whitespace-pre-line body-14-regular text-new-gray-500'>삭제된 댓글입니다.</div>
            ) : (
              <div className='w-full whitespace-pre-line body-14-regular text-new-gray-900'>{comment?.content}</div>
            )}
          </div>
        </div>

        {comment.children && comment.children.length > 0 && (
          <div className='w-full'>
            {filterNullUndefined(comment.children).map((child) => (
              <TripPostComment
                key={`post_${postId}_comment_${child?.id}`}
                postId={postId}
                comment={child}
                onClickReportComment={onClickReportComment}
                isParent={false}
                isLoggedin={isLoggedin}
              />
            ))}
          </div>
        )}

        {isReplyMode && (
          <div className={`w-full`}>
            <div className='w-full px-20pxr flex items-center gap-8pxr border-b border-new-gray-200'>
              <div className='shrink-0 flex items-center justify-center w-20pxr h-20pxr'>
                <ReplyComment1010 />
              </div>
              <div className='flex-1 h-50pxr flex justify-between items-center gap-8pxr'>
                <input
                  type='text'
                  placeholder={`${isLoggedin ? '불쾌감을 주는 욕설과 악플은 삭제될 수 있습니다.' : '로그인 후 댓글을 작성할 수 있습니다.'}`}
                  disabled={!isLoggedin}
                  value={replyCommentValue}
                  onChange={(e) => {
                    if (e.target.value.length > 1000) return;
                    setReplyCommentValue(e.target.value);
                  }}
                  className={`flex-1 outline-none body-14-regular text-new-gray-900 bg-white placeholder:text-new-gray-500`}
                />
                <button
                  className={`shrink-0 w-50pxr h-32pxr flex items-center justify-center rounded-6pxr text-white body-14-md ${
                    replyCommentValue.length === 0 ? 'bg-new-gray-300' : 'bg-new-point-500'
                  }`}
                  disabled={replyCommentValue.length === 0}
                  onClick={() => onClickSubmitReplyComment()}
                >
                  등록
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
